/**
 * website-chat-tab.tsx — Quản lý nút chat nhúng trên website.
 *
 * Một tổ chức khai nhiều website, mỗi cái một `siteKey` riêng — nhờ vậy biết
 * khách đến từ trang nào ("Web: Landing khuyến mãi") và đặt màu/lời chào khác nhau.
 *
 * Khung xem trước nhúng **chính script thật** qua iframe, không vẽ lại bằng
 * React: vẽ lại chỉ giống hình thức, không chứng minh được script chạy được.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Copy, Globe, Loader2, Plus, Trash2, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea, Checkbox } from '@/components/ui/misc'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import {
  useWidgets, useCreateWidget, useUpdateWidget, useDeleteWidget, type WebsiteWidget,
} from '@/hooks/use-integrations'
import { API_ORIGIN } from '@/lib/config'
import { api, apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { SelectableTable } from './accounts-table'
import { noConnectHint, useAutoAdd } from './channel-catalog'
import { useMyPermissions } from '@/hooks/use-settings'
import { useOnboardingReturn } from '@/hooks/use-onboarding-return'
import { contactError, previewConfig } from '@/lib/widget-contacts'
import { ContactLinksEditor } from './widget-contacts-editor'

/** Mascot vẫy tay — icon mặc định nút chat khi chưa tải logo (cùng ảnh nhúng trong widget.js). */
const MASCOT_SRC = '/assets/mascot-chat.webp'

function embedSnippet(siteKey: string): string {
  return `<script src="${API_ORIGIN}/widget.js" data-site-key="${siteKey}" defer></script>`
}

/** Escape để ghép an toàn vào thuộc tính HTML trong `srcDoc` — `siteKey` luôn là hex server sinh, chỉ phòng hờ. */
function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function CopyRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const { t } = useTranslation('chanSocial')
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <div className="mt-0.5 flex items-start gap-1.5">
        <code className={cn('min-w-0 flex-1 rounded bg-muted px-2 py-1.5 text-[11px]', mono && 'break-all')}>
          {value}
        </code>
        <Button
          variant="outline" size="sm" className="h-7 shrink-0 px-2"
          onClick={() =>
            navigator.clipboard.writeText(value).then(
              () => toast.success(t('website.copied')),
              () => toast.error(t('website.copyBlocked')),
            )
          }
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}

/**
 * Chọn logo: tải ảnh lên máy chủ rồi lưu URL. Không cho dán URL ngoài — ảnh nằm
 * ở máy chủ người khác thì hỏng lúc nào không biết, mà widget chạy trên website
 * của khách nên ảnh hỏng là mất mặt ngay trang chủ.
 */
function LogoPicker({
  value, color, onChange, readOnly,
}: {
  value: string | null
  color: string
  onChange: (url: string | null) => void
  /** Thiếu `integrations.update` (POST /widgets/upload-logo bị 403) → chỉ xem, ẩn nút tải/gỡ. */
  readOnly?: boolean
}) {
  const { t } = useTranslation('chanSocial')
  const [busy, setBusy] = useState(false)

  async function upload(file: File) {
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      // Bắt buộc ghi đè Content-Type: `api` mặc định là application/json, gửi
      // FormData kèm header đó thì Fastify từ chối bằng 406.
      const { data } = await api.post<{ url: string }>('/widgets/upload-logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onChange(data.url)
      toast.success(t('website.logo.uploaded'))
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shrink-0 text-center">
      <div
        className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border"
        style={{ background: value ? '#fff' : color }}
      >
        {value ? (
          <img src={value} alt="Logo" className="h-full w-full object-cover" />
        ) : (
          <img src={MASCOT_SRC} alt={t('website.logo.mascotAlt')} className="mt-1.5 h-[118%] w-[118%] max-w-none object-contain" />
        )}
        {busy && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/40">
            <Loader2 className="h-5 w-5 animate-spin text-white" />
          </span>
        )}
      </div>
      {!readOnly && (
        <>
          <div className="mt-1.5 flex items-center justify-center gap-1">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  // Gán lại value XOÁ luôn files → phải lấy tệp ra trước khi reset.
                  e.target.value = ''
                  if (f) void upload(f)
                }}
              />
              <span className="inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] hover:bg-accent">
                <Upload className="h-3 w-3" /> {value ? t('common:actions.change') : t('website.logo.upload')}
              </span>
            </label>
            {value && (
              <button
                type="button"
                onClick={() => onChange(null)}
                title={t('website.logo.remove')}
                className="inline-flex items-center rounded border px-1.5 py-1 text-[11px] hover:bg-accent"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground">{t('website.logo.limits')}</p>
        </>
      )}
      {!value && <p className="text-[10px] text-muted-foreground">{t('website.logo.none')}</p>}
    </div>
  )
}

/**
 * Xem trước bằng cách nạp **script thật** vào iframe (mở sẵn cửa sổ). Cấu hình đang sửa (chưa lưu) gửi vào
 * qua postMessage (`data-preview="1"`) nên khung đổi ngay khi gõ; tin nhắn thử vẫn vào thẳng inbox.
 */
function WidgetPreview({ widget }: { widget: WebsiteWidget }) {
  const { t } = useTranslation('chanSocial')
  // widget.js được đặt cache 5 phút cho website khách. Trong admin thì ngược
  // lại: xem trước phải luôn là bản mới nhất, nếu không sẽ thử một bản cũ và
  // kết luận sai. `nonce` đổi mỗi lần mở lại hoặc bấm "Tải lại".
  const [nonce, setNonce] = useState(() => Date.now())
  const frame = useRef<HTMLIFrameElement>(null)
  const [ready, setReady] = useState(false)
  const config = useMemo(() => previewConfig(widget), [widget])
  const srcDoc = useMemo(
    () => `<!doctype html><html><head><meta charset="utf-8"><style>
      html,body{margin:0;height:100%;background:#eef2f7;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif}
      .hint{position:absolute;top:12px;left:12px;right:12px;color:#94a3b8;font-size:12px;line-height:1.5}
    </style></head><body>
      <p class="hint">${t('website.preview.mockPage')}</p>
      <script src="${API_ORIGIN}/widget.js?v=${nonce}" data-site-key="${escapeHtmlAttr(widget.siteKey)}" data-api="${API_ORIGIN}" data-open="1" data-preview="1"></script>
    </body></html>`,
    [widget.siteKey, nonce, t],
  )
  // Gửi cấu hình nháp (chờ 250ms sau lần gõ cuối để không vẽ lại liên tục).
  useEffect(() => {
    if (!ready) return
    const timer = setTimeout(() => {
      // vbsec L41: sandbox của iframe dưới đây không còn `allow-same-origin` → origin của khung là
      // "null" (unique opaque origin) — địa chỉ nhận duy nhất khớp được là chuỗi 'null', không phải '*'
      // (tránh gửi cấu hình nháp cho bất kỳ listener nào nếu khung từng bị điều hướng).
      frame.current?.contentWindow?.postMessage({ type: 'ecdp-widget-preview', config }, 'null')
    }, 250)
    return () => clearTimeout(timer)
  }, [config, ready])

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{t('website.preview.live')}</p>
        <Button variant="outline" size="sm" className="h-7 shrink-0 px-2 text-xs"
          onClick={() => { setReady(false); setNonce(Date.now()) }}>
          {t('common:actions.reload')}
        </Button>
      </div>
      <iframe
        ref={frame}
        key={`${widget.siteKey}:${nonce}`}
        srcDoc={srcDoc}
        onLoad={() => setReady(true)}
        title={t('website.preview.iframeTitle', { name: widget.name })}
        // vbsec L41: KHÔNG thêm allow-same-origin — iframe dùng `srcDoc` nên nếu có allow-same-origin,
        // script trong khung chạy với CÙNG origin trang admin (đọc được cookie/localStorage/DOM của
        // chính ứng dụng, tương đương XSS toàn trang) chứ không phải origin của widget.js. Thiếu cờ này
        // script vẫn gọi API_ORIGIN bình thường (fetch cross-origin không bị sandbox chặn).
        // allow-forms BẮT BUỘC: ô nhập tin là <form>; thiếu quyền này trình duyệt
        // chặn thẳng sự kiện submit → bấm Gửi không có phản ứng gì.
        sandbox="allow-scripts allow-popups allow-forms"
        className="h-[700px] w-full rounded-lg border bg-muted"
      />
    </div>
  )
}

function WidgetEditor({ widget }: { widget: WebsiteWidget }) {
  const { t } = useTranslation('chanSocial')
  const update = useUpdateWidget()
  const remove = useDeleteWidget()
  const [draft, setDraft] = useState<WebsiteWidget>(() => ({
    ...widget, homeTitle: widget.homeTitle ?? null, contactLinks: widget.contactLinks ?? [],
  }))
  const [saving, setSaving] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  // Lưu cấu hình / tải logo = update, xoá website = delete — người chỉ có quyền xem vẫn mở được để chép mã nhúng.
  const { data: perms } = useMyPermissions()
  const canUpdate = !!perms?.has('integrations.update')
  const canDelete = !!perms?.has('integrations.delete')

  const set = <K extends keyof WebsiteWidget>(k: K, v: WebsiteWidget[K]) =>
    setDraft((d) => ({ ...d, [k]: v }))

  const save = async () => {
    const bad = draft.contactLinks.find((c) => contactError(c))
    if (bad) {
      setShowErrors(true)
      return toast.error(t('website.editor.contactError', { label: bad.label || bad.type, error: contactError(bad) }))
    }
    setSaving(true)
    try {
      await update.mutateAsync({
        id: draft.id, name: draft.name, domains: draft.domains, isActive: draft.isActive,
        title: draft.title, greeting: draft.greeting, primaryColor: draft.primaryColor,
        displayName: draft.displayName ?? '', logoUrl: draft.logoUrl ?? '',
        position: draft.position, liveChatEnabled: draft.liveChatEnabled,
        homeTitle: draft.homeTitle ?? '',
        contactLinks: draft.contactLinks.map((c) => ({ ...c, label: c.label.trim(), url: c.url.trim() })),
      })
      setShowErrors(false)
      toast.success(t('website.editor.saved'))
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setSaving(false)
    }
  }

  const del = async () => {
    if (!window.confirm(t('website.editor.confirmDelete', { name: draft.name }))) return
    try {
      await remove.mutateAsync(draft.id)
      toast.success(t('website.editor.deleted'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
      <div className="min-w-0 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>{t('website.editor.name')}</Label>
            <Input value={draft.name} onChange={(e) => set('name', e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>{t('website.editor.position')}</Label>
            <Select value={draft.position} onValueChange={(v) => set('position', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="right">{t('website.editor.positionRight')}</SelectItem>
                <SelectItem value="left">{t('website.editor.positionLeft')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>{t('website.editor.domains')}</Label>
          <Textarea
            value={draft.domains.join('\n')}
            onChange={(e) => set('domains', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
            placeholder={'evotech.vn\nshop.evotech.vn'}
            className="mt-1 min-h-[68px] font-mono text-xs"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            <Trans t={t} i18nKey="website.editor.domainsHint" components={{ b: <strong /> }} />
          </p>
        </div>

        <div className="rounded-lg border p-3">
          <p className="mb-2 text-xs font-semibold">{t('website.editor.identity')}</p>
          <div className="flex items-start gap-3">
            <LogoPicker
              value={draft.logoUrl}
              color={draft.primaryColor}
              onChange={(url) => set('logoUrl', url)}
              readOnly={!canUpdate}
            />
            <div className="min-w-0 flex-1 space-y-2">
              <div>
                <Label className="text-[11px]">{t('website.editor.displayName')}</Label>
                <Input
                  value={draft.displayName ?? ''}
                  onChange={(e) => set('displayName', e.target.value)}
                  placeholder={draft.title || t('website.editor.brandPlaceholder')}
                  className="mt-1"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t('website.editor.displayNameHint')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>{t('website.editor.chatTitle')}</Label>
            <Input value={draft.title} onChange={(e) => set('title', e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>{t('website.editor.color')}</Label>
            <div className="mt-1 flex gap-2">
              <input
                type="color" value={draft.primaryColor}
                onChange={(e) => set('primaryColor', e.target.value)}
                className="h-9 w-12 cursor-pointer rounded border bg-background"
              />
              <Input value={draft.primaryColor} onChange={(e) => set('primaryColor', e.target.value)} />
            </div>
          </div>
        </div>

        <div>
          <Label>{t('website.editor.greeting')}</Label>
          <Textarea value={draft.greeting} onChange={(e) => set('greeting', e.target.value)} className="mt-1 min-h-[56px]" />
        </div>

        <div className="space-y-3 rounded-lg border p-3">
          <div>
            <p className="text-xs font-semibold">{t('website.editor.homeSection')}</p>
            <p className="text-[11px] text-muted-foreground">
              {t('website.editor.homeSectionHint')}
            </p>
          </div>
          <div>
            <Label className="text-[11px]">{t('website.editor.homeTitle')}</Label>
            <Textarea
              value={draft.homeTitle ?? ''}
              onChange={(e) => set('homeTitle', e.target.value)}
              placeholder={`👋 Xin chào, cảm ơn bạn đã liên hệ ${draft.displayName?.trim() || draft.title}!`} // i18n-ignore: lời chào mặc định gửi cho khách cuối (dữ liệu widget)
              maxLength={120}
              className="mt-1 min-h-[56px]"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">{t('website.editor.homeTitleHint')}</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={draft.liveChatEnabled} onCheckedChange={(v) => set('liveChatEnabled', v === true)} />
            {t('website.editor.liveChat')}
          </label>
          <div>
            <Label className="text-[11px]">{t('website.editor.contacts')}</Label>
            <div className="mt-1">
              <ContactLinksEditor value={draft.contactLinks} onChange={(v) => set('contactLinks', v)} showErrors={showErrors} />
            </div>
          </div>
        </div>

        <CopyRow label={t('website.editor.embedLabel')} value={embedSnippet(draft.siteKey)} mono />

        {(canUpdate || canDelete) && (
          <div className="flex items-center gap-2">
            {canUpdate && (
              <>
                <Button onClick={save} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t('shared.saveConfig')}
                </Button>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={draft.isActive} onCheckedChange={(v) => set('isActive', v === true)} />
                  {t('common:status.enabled')}
                </label>
              </>
            )}
            {canDelete && (
              <Button variant="ghost" size="sm" className="ml-auto text-destructive" onClick={del}>
                <Trash2 className="h-4 w-4" /> {t('website.editor.delete')}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="xl:sticky xl:top-4 xl:self-start">
        <p className="mb-1.5 text-xs font-semibold">{t('website.editor.previewHeading')}</p>
        <WidgetPreview widget={draft} />
        <p className="mt-1.5 text-[11px] text-muted-foreground">
          <Trans t={t} i18nKey="website.editor.previewNote" components={{ b: <strong /> }} />
          {canUpdate && <Trans t={t} i18nKey="website.editor.previewNoteSave" components={{ b: <strong /> }} />}
        </p>
      </div>
    </div>
  )
}

export function WebsiteChatTab() {
  const { t } = useTranslation('chanSocial')
  const { data: widgets, isLoading, isError } = useWidgets()
  const create = useCreateWidget()
  const [name, setName] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const onboardingReturn = useOnboardingReturn()
  // Thêm website = POST /widgets → cần `integrations.create`; Cấu hình (mở trình sửa) cần `integrations.update`.
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('integrations.create')
  const canUpdate = !!perms?.has('integrations.update')
  // "Thêm" ở lưới kênh → đặt con trỏ vào ô tên website mới.
  useAutoAdd(() => nameRef.current?.focus(), !isLoading)

  const add = async () => {
    if (!name.trim()) return toast.error(t('website.tab.nameRequired'))
    try {
      const w = await create.mutateAsync({ name: name.trim() })
      setName('')
      setOpenId(w.id)
      toast.success(t('website.tab.created', { name: w.name }))
      // Widget bật ngay = kênh đã kết nối. Chờ lâu hơn mặc định để kịp chép mã nhúng vừa hiện.
      onboardingReturn.complete(1, { delayMs: 5000 })
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  if (isLoading) return <Loading label={t('website.tab.loading')} />
  if (isError) return <ErrorState message={t('website.tab.error')} />

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('website.tab.intro')}
        </p>
        {canCreate && (
          <div className="flex gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add() }}
              ref={nameRef}
              placeholder={t('website.tab.newNamePlaceholder')}
              className="w-48"
            />
            <Button onClick={add} disabled={create.isPending}>
              {create.isPending ? <Loader2 className="animate-spin" /> : <Plus />} {t('website.tab.add')}
            </Button>
          </div>
        )}
      </div>

      <SelectableTable
        rows={widgets}
        rowKey={(w) => w.id}
        itemLabel={t('website.tab.itemLabel')}
        searchPlaceholder={t('website.tab.searchPlaceholder')}
        searchText={(w) => [w.name, ...w.domains].join(' ')}
        filters={[
          { value: 'on', label: t('common:status.enabled'), match: (w) => w.isActive },
          { value: 'off', label: t('common:status.disabled'), match: (w) => !w.isActive },
        ]}
        empty={
          <EmptyState
            icon={Globe}
            title={t('website.tab.emptyTitle')}
            description={canCreate ? t('website.tab.emptyDescription') : noConnectHint()}
          />
        }
        columns={[
          {
            key: 'site',
            header: t('common:fields.website'),
            cell: (w) => (
              <div className="flex min-w-[180px] items-center gap-3">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white"
                  style={{ backgroundColor: w.primaryColor }}
                >
                  <Globe className="h-4 w-4" />
                </span>
                <span className="truncate font-medium">{w.name}</span>
              </div>
            ),
          },
          {
            key: 'domains',
            header: t('website.tab.colDomains'),
            cell: (w) =>
              w.domains.length ? (
                <span className="block max-w-[260px] truncate" title={w.domains.join(', ')}>
                  {w.domains.join(', ')}
                </span>
              ) : (
                <span className="text-muted-foreground">{t('website.tab.anywhere')}</span>
              ),
          },
          {
            key: 'conv',
            header: t('website.tab.colConversations'),
            className: 'tabular-nums',
            cell: (w) => w.conversationCount ?? 0,
          },
          {
            key: 'status',
            header: t('common:fields.status'),
            cell: (w) =>
              w.isActive ? <Badge variant="success">{t('common:status.enabled')}</Badge> : <Badge variant="secondary">{t('common:status.disabled')}</Badge>,
          },
          {
            key: 'actions',
            header: t('common:fields.actions'),
            className: 'w-px whitespace-nowrap text-right',
            sticky: true,
            cell: (w) => (
              <Button
                variant={openId === w.id ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setOpenId(openId === w.id ? null : w.id)}
              >
                {openId === w.id ? t('common:actions.showLess') : canUpdate ? t('website.tab.configure') : t('common:actions.view')}
              </Button>
            ),
          },
        ]}
        renderDetail={(w) =>
          openId === w.id ? (
            <div className="rounded-lg border bg-background p-4">
              <WidgetEditor widget={w} />
            </div>
          ) : null
        }
      />
    </div>
  )
}
